import type { AccountProduct } from '../features/product-segment'
// Authenticated app shell — sidebar + content, ported to match the original
// index.html structure: brand header + close, user row on top, icon nav with a
// NEW badge + divider + logout item, and a per-section brand header in content.

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useProfileStore } from '../store/profileStore'
import { useAuth } from '../hooks/useAuth'
import { usePaymentSuccess } from '../hooks/usePaymentSuccess'
import { useOnboardingGate } from '../hooks/useOnboardingGate'
import { usePushNotifications } from '../hooks/usePushNotifications'
import { useChatwootStore } from '../store/chatwootStore'
import * as metrica from '../lib/metrica'
import { isNative } from '../lib/platform'
import { NAV } from './appShellNav'
import type { NavItem } from './appShellNav'
import styles from './AppShell.module.css'
import { acquirePaymentAppResume } from '../payments/channel'
import { analytics, useAnalyticsScreen } from '../lib/analytics'

// Mirrors the original sidebar-nav (icons + labels + badge + divider + logout).

// data-section keys for the tab_open Metrica goal (original sidebar.js:49).
const SECTION_KEY = {
  '/': 'chat',
  '/practices': 'practices',
  '/psychologist': 'psychologist',
  '/pricing': 'pricing',
  '/faq': 'faq',
  '/support': 'support',
  '/install': 'install',
  '/settings': 'settings',
}

export default function AppShell({ profile, nav = NAV, scenario }: { profile: AccountProduct; nav?: NavItem[]; scenario?: { id: string; version: number; completionPath?: string } }) {
  const [open, setOpen] = useState(false)
  const displayName = useAuthStore((s) => s.displayName)
  const userId = useAuthStore((s) => s.userId)
  const stats = useProfileStore((s) => s.stats)
  const loadStats = useProfileStore((s) => s.loadStats)
  const { logout } = useAuth()
  // Chat manages its own full-width scroll (.chat-container); other sections
  // scroll inside a --screen-max-capped, centered container (.section-content).
  const location = useLocation()
  const isChat = location.pathname === '/'
  const outletRef = useRef<HTMLDivElement | null>(null)

  // Reset the section scroll container to the top on every route change. The
  // .outlet div persists across route swaps (only <Outlet/>'s children change),
  // so its scrollTop carries over — without this, a new section opens at the
  // previous section's scroll offset. useLayoutEffect resets before paint so
  // there's no flash of the scrolled-down position.
  useLayoutEffect(() => {
    outletRef.current?.scrollTo(0, 0)
  }, [location.pathname])

  usePaymentSuccess()
  const { ready, error, retry } = useOnboardingGate(profile, scenario?.id, scenario?.version, scenario?.completionPath)
  useAnalyticsScreen(location.pathname, ready, profile.segment)
  const { refresh: refreshPush } = usePushNotifications()
  const cwUnread = useChatwootStore((s) => s.unread)
  const cwShow = useChatwootStore((s) => s.hasConversation && !s.resolved && s.unread > 0)

  // Subscription state drives sidebar visibility (hide "Тарифы" for subscribers,
  // original sidebar.js:16-23 + onboarding.js:127-128).
  useEffect(() => {
    loadStats()
  }, [loadStats])

  // The authenticated shell owns one reference-counted recovery listener lease.
  // Pending StrictMode cleanup is released as soon as registration completes.
  useEffect(() => {
    let cancelled = false
    let release: (() => Promise<void>) | undefined
    void acquirePaymentAppResume().then((acquiredRelease) => {
      if (cancelled) void acquiredRelease().catch(() => {})
      else release = acquiredRelease
    }).catch(() => {})
    return () => {
      cancelled = true
      if (release) void release().catch(() => {})
    }
  }, [])

  // Re-validate this browser's existing subscription on load. The account-level
  // setting cannot be used as a gate because push is enabled per device.
  // Native checks the OS permission after onboarding: granted users re-bind FCM
  // to the current account, while never-asked users see the system dialog.
  useEffect(() => {
    if (isNative) return
    refreshPush().catch(() => {})
  }, [refreshPush])

  useEffect(() => {
    if (!isNative || !ready) return
    refreshPush().catch(() => {})
  }, [ready, refreshPush, userId])

  // Chatwoot unread badge: fetch once, then poll every 60s. WS pushes keep it
  // live when connected, but on mobile the WS drops silently and pushes stop —
  // the poll keeps the badge current regardless (parent bd-eiw). Runs while the
  // widget is open too: the 60s GET is cheap and markRead's optimistic clear
  // wins the rare race.
  useEffect(() => {
    useChatwootStore.getState().fetch()
    const id = setInterval(() => useChatwootStore.getState().fetch(), 60000)
    return () => clearInterval(id)
  }, [])
  const hasSubscription = Boolean(stats?.subscription_status)

  const navItems = nav.filter((item) => !(item.to === '/pricing' && hasSubscription))

  // Breakup branding (mattermost 27.10): Russian brand spelling with the
  // product caption, matching the onboarding header; humanteq stays as-is.
  const breakUpBrand = profile.segment === 'break_up'
  const brandTitle = breakUpBrand ? 'Хьюмантек' : 'Humanteq'
  const brandRole = breakUpBrand ? 'ИИ-сопровождение в расставаниях' : null

  const onNav = (to: string) => {
    setOpen(false)
    if (to === '/sos') analytics.track('sosClicked')
    const key = SECTION_KEY[to as keyof typeof SECTION_KEY]
    if (key) metrica.reachGoal('tab_open', { section: key })
  }

  if (!ready) {
    return <div className={styles.gateLoading}>{error ? <div><p role="alert">{error}</p><button onClick={retry}>Повторить</button></div> : <div className={styles.gateSpinner} />}</div>
  }

  return (
    <div className={styles.layout}>
      <nav className={`${styles.sidebar} ${open ? styles.open : ''}`}>
        <header className={styles.sidebarHeader}>
          <div className={styles.brand}>
            <img className={styles.brandLogo} src="/assets/app-icon.png" alt="" />
            <div className={styles.brandText}>
              <span className={styles.brandTitle}>{brandTitle}</span>
              {brandRole && <span className={styles.brandRole}>{brandRole}</span>}
            </div>
          </div>
          <button className={styles.closeBtn} onClick={() => setOpen(false)} aria-label="Закрыть">×</button>
        </header>

        <div className={styles.user}>
          <img className={styles.userAvatar} src="/assets/avatar.svg" alt="" />
          <div className={styles.userName}>{displayName || 'Пользователь'}</div>
        </div>

        <div className={styles.nav}>
          {navItems.map((item, i) =>
            item.divider ? (
              <div key={`div-${i}`} className={styles.divider} />
            ) : (
              <NavLink
                key={item.to}
                to={item.to!}
                end={item.end}
                className={({ isActive }) => `${styles.navItem} ${item.utility ? styles.utility : ''} ${isActive ? styles.active : ''}`}
                onClick={() => onNav(item.to!)}
              >
                <span className={styles.navLeft}>
                  {item.icon && <img className={styles.navIcon} src={`/assets/${item.icon}`} alt="" />}
                  <span>{item.label}</span>
                </span>
                {item.badge && <span className={styles.badgeNew}>{item.badge}</span>}
                {item.chatwoot && cwShow && (
                  <span className={styles.chatwootBadge}>{cwUnread > 9 ? '9+' : cwUnread}</span>
                )}
                {item.chatBadge && <span className={styles.badge} hidden />}
              </NavLink>
            )
          )}
          <button className={`${styles.navItem} ${styles.utility} ${styles.logout}`} onClick={logout}>
            <span className={styles.navLeft}>
              <span>Выйти</span>
            </span>
          </button>
        </div>
      </nav>

      {open && <div className={styles.overlay} onClick={() => setOpen(false)} />}

      <main className={styles.content}>
        <header className={styles.sectionHeader}>
          <div className={styles.brand}>
            <img className={styles.sectionLogo} src="/assets/app-icon.png" alt="" />
            <div className={styles.brandText}>
              <span className={styles.brandTitle}>{brandTitle}</span>
              {brandRole && <span className={styles.brandRole}>{brandRole}</span>}
            </div>
          </div>
          <button className={styles.hamburger} onClick={() => setOpen(true)} aria-label="Меню">
            <span /><span /><span />
          </button>
        </header>
        <div ref={outletRef} data-testid="section-scroll" className={`${styles.outlet} ${isChat ? styles.outletChat : styles.outletSection}`}>
          <Outlet />
        </div>
      </main>
    </div>
  )
}
